import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './fire-weather.css';

const CACHE_KEY = 'smfd-fire-weather-cache-v1';
const LOCATION_KEY = 'smfd-fire-weather-location-v1';
const REFRESH_MS = 60 * 60 * 1000;
const COORD_STEP = 0.02;

function onlineNow() {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

function coarseCoordinate(value) {
  return Math.round(Number(value) / COORD_STEP) * COORD_STEP;
}

function readCache() {
  try {
    const parsed = JSON.parse(window.localStorage?.getItem(CACHE_KEY) || 'null');
    return parsed?.data?.ok ? parsed : null;
  } catch (_) { return null; }
}

function writeCache(data) {
  try { window.localStorage?.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), data })); } catch (_) {}
}

function readSavedLocation() {
  try {
    const parsed = JSON.parse(window.localStorage?.getItem(LOCATION_KEY) || 'null');
    const latitude = Number(parsed?.latitude);
    const longitude = Number(parsed?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    return { latitude, longitude };
  } catch (_) { return null; }
}

function writeSavedLocation(coords) {
  const latitude = coarseCoordinate(coords.latitude);
  const longitude = coarseCoordinate(coords.longitude);
  try { window.localStorage?.setItem(LOCATION_KEY, JSON.stringify({ latitude, longitude, savedAt: Date.now() })); } catch (_) {}
  return { latitude, longitude };
}

function ageText(value) {
  const stamp = Date.parse(value || '');
  if (!Number.isFinite(stamp)) return 'unknown age';
  const mins = Math.max(0, Math.round((Date.now() - stamp) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  const remainder = mins % 60;
  return remainder ? `${hours}h ${remainder}m ago` : `${hours}h ago`;
}

function metric(value, suffix = '') {
  return value == null || value === '' ? '—' : `${value}${suffix}`;
}

function numeric(items, key) {
  return items.map(item => Number(item?.[key])).filter(Number.isFinite);
}
function maxValue(items, key) { const values = numeric(items, key); return values.length ? Math.max(...values) : null; }
function minValue(items, key) { const values = numeric(items, key); return values.length ? Math.min(...values) : null; }

function windText(direction, speed, gust) {
  if (speed == null && !direction) return '—';
  const parts = [direction || 'VRB', speed == null ? '' : `${speed} mph`].filter(Boolean);
  if (gust != null) parts.push(`G${gust}`);
  return parts.join(' ');
}

function transportText(row) { return windText(row?.transportWindDirectionText, row?.transportWindSpeedMph, null); }

function timeLabel(value, timeZone, options = {}) {
  const stamp = Date.parse(value || '');
  if (!Number.isFinite(stamp)) return '—';
  return new Intl.DateTimeFormat(undefined, { timeZone: timeZone || undefined, hour: 'numeric', ...options }).format(new Date(stamp));
}

function dateTimeLabel(value, timeZone) {
  const stamp = Date.parse(value || '');
  if (!Number.isFinite(stamp)) return '—';
  return new Intl.DateTimeFormat(undefined, { timeZone: timeZone || undefined, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(stamp));
}

function isFireAlert(alert) {
  const text = `${alert?.event || ''} ${alert?.headline || ''}`.toLowerCase();
  return /red flag|fire weather|extreme fire|fire danger/.test(text);
}

function historyWithin(items, hours) {
  const cutoff = Date.now() - hours * 60 * 60 * 1000;
  return items.filter(item => Date.parse(item?.timestamp || '') >= cutoff);
}

function QuickMetric({ label, value, detail }) {
  return <div className="firewx-metric"><span>{label}</span><strong>{value}</strong>{detail ? <small>{detail}</small> : null}</div>;
}

function ObservationTrend({ hours, rows }) {
  const subset = historyWithin(rows, hours);
  const start = subset[0];
  const end = subset[subset.length - 1];
  const rhStart = Number(start?.relativeHumidity);
  const rhEnd = Number(end?.relativeHumidity);
  const tempStart = Number(start?.temperatureF);
  const tempEnd = Number(end?.temperatureF);
  return (
    <div className="firewx-summary-card">
      <span className="firewx-kicker">PAST {hours} HOURS · OBSERVED</span>
      <div className="firewx-summary-values">
        <QuickMetric label="Min RH" value={metric(minValue(subset, 'relativeHumidity'), '%')} detail={Number.isFinite(rhStart) && Number.isFinite(rhEnd) ? `${rhStart}% → ${rhEnd}%` : ''} />
        <QuickMetric label="Max Temp" value={metric(maxValue(subset, 'temperatureF'), '°')} detail={Number.isFinite(tempStart) && Number.isFinite(tempEnd) ? `${tempStart}° → ${tempEnd}°` : ''} />
        <QuickMetric label="Max Wind" value={metric(maxValue(subset, 'windSpeedMph'), ' mph')} />
        <QuickMetric label="Max Gust" value={metric(maxValue(subset, 'windGustMph'), ' mph')} />
      </div>
      <small>{subset.length ? `${subset.length} NWS/MADIS observations` : 'Observation history unavailable'}</small>
    </div>
  );
}

export default function FireWeather({ offline = false }) {
  const initialCache = useMemo(() => readCache(), []);
  const initialLocation = useMemo(() => readSavedLocation(), []);
  const [data, setData] = useState(initialCache?.data || null);
  const [status, setStatus] = useState(initialCache ? 'cached' : initialLocation ? 'loading' : 'locating');
  const [message, setMessage] = useState(initialCache ? 'Showing the last saved NWS forecast while an update is checked.' : initialLocation ? 'Loading NWS fire weather for the saved forecast location…' : 'Getting phone location for the first fire-weather forecast…');
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const dataRef = useRef(initialCache?.data || null);
  const lastCoordsRef = useRef(initialLocation);
  const fetchedAtRef = useRef(initialCache?.data?.fetchedAt || '');

  useEffect(() => { dataRef.current = data; fetchedAtRef.current = data?.fetchedAt || ''; }, [data]);

  const fetchForecast = useCallback(async (coords, force = false) => {
    const existingData = dataRef.current;
    if (!onlineNow()) {
      setStatus(existingData ? 'offline' : 'error');
      setMessage(existingData ? 'Offline — showing the last forecast saved on this device.' : 'No service and no fire-weather forecast has been saved on this device yet.');
      return;
    }

    const savedCoords = writeSavedLocation(coords);
    const lat = savedCoords.latitude;
    const lon = savedCoords.longitude;
    lastCoordsRef.current = savedCoords;

    const existingStamp = Date.parse(fetchedAtRef.current || '');
    const fresh = Number.isFinite(existingStamp) && Date.now() - existingStamp < REFRESH_MS;
    const sameGrid = existingData?.location && Math.abs(Number(existingData.location.latitude) - lat) < 0.011 && Math.abs(Number(existingData.location.longitude) - lon) < 0.011;
    if (!force && fresh && sameGrid) {
      setStatus('ready');
      setMessage('Saved NWS forecast is less than one hour old.');
      return;
    }

    setRefreshing(true);
    setError('');
    setStatus(existingData ? 'refreshing' : 'loading');
    setMessage(existingData ? 'Checking NWS for updated forecast and observations…' : 'Loading NWS fire-weather grid and nearby observations…');
    try {
      const response = await fetch('/api/fire-weather', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lat, lon }), cache: 'no-store' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.ok) throw new Error(payload?.error || 'Unable to load NWS fire weather.');
      dataRef.current = payload;
      setData(payload);
      fetchedAtRef.current = payload.fetchedAt || '';
      writeCache(payload);
      setStatus('ready');
      setMessage('Current NWS digital fire-weather forecast and linked observation data.');
    } catch (err) {
      setError(err?.message || 'Unable to refresh fire weather.');
      setStatus(existingData ? 'cached' : 'error');
      setMessage(existingData ? 'Update failed — the last saved forecast remains available.' : 'Unable to load a fire-weather forecast.');
    } finally { setRefreshing(false); }
  }, []);

  const locateAndRefresh = useCallback((force = false) => {
    const existingData = dataRef.current;
    if (!navigator.geolocation) {
      setStatus(existingData ? 'cached' : 'error');
      setMessage(existingData ? 'Location services are unavailable — showing saved fire weather.' : 'This device does not provide location services.');
      return;
    }
    if (!onlineNow() && existingData) {
      setStatus('offline');
      setMessage('Offline — showing the last forecast saved on this device.');
      return;
    }

    setError('');
    setStatus(existingData ? 'cached' : 'locating');
    setMessage(existingData ? 'Updating the saved forecast location…' : 'Getting phone location…');
    navigator.geolocation.getCurrentPosition(
      position => fetchForecast(position.coords, force),
      geoError => {
        const denied = geoError?.code === 1;
        setError(denied ? 'Location permission is required to update the saved Fire Weather location.' : 'Unable to determine the phone location.');
        setStatus(existingData ? 'cached' : 'error');
        setMessage(existingData ? 'Showing the last saved forecast.' : 'Enable location access and try again.');
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 5 * 60 * 1000 }
    );
  }, [fetchForecast]);

  const refreshSavedForecast = useCallback((force = true) => {
    if (lastCoordsRef.current) fetchForecast(lastCoordsRef.current, force);
    else locateAndRefresh(force);
  }, [fetchForecast, locateAndRefresh]);

  useEffect(() => {
    if (lastCoordsRef.current) fetchForecast(lastCoordsRef.current, false);
    else locateAndRefresh(false);

    const timer = window.setInterval(() => {
      const stamp = Date.parse(fetchedAtRef.current || '');
      if (onlineNow() && lastCoordsRef.current && (!Number.isFinite(stamp) || Date.now() - stamp >= REFRESH_MS)) fetchForecast(lastCoordsRef.current, true);
    }, 5 * 60 * 1000);
    const handleOnline = () => { if (lastCoordsRef.current) fetchForecast(lastCoordsRef.current, false); };
    const handleVisibility = () => { if (document.visibilityState === 'visible' && lastCoordsRef.current) fetchForecast(lastCoordsRef.current, false); };
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => { window.clearInterval(timer); window.removeEventListener('online', handleOnline); document.removeEventListener('visibilitychange', handleVisibility); };
  }, [fetchForecast, locateAndRefresh]);

  const timeZone = data?.location?.timeZone;
  const hourly = data?.hourly || [];
  const next12 = hourly.slice(0, 12);
  const next24 = hourly.slice(0, 24);
  const current = hourly[0] || {};
  const observation = data?.observation;
  const station = data?.station;
  const observationHistory = data?.observationHistory || [];
  const alerts = data?.alerts || [];
  const fireAlerts = alerts.filter(isFireAlert);
  const otherAlerts = alerts.filter(alert => !isFireAlert(alert));
  const generalByHour = useMemo(() => {
    const map = new Map();
    for (const item of data?.generalHourly || []) {
      const stamp = Date.parse(item.startTime || '');
      if (Number.isFinite(stamp)) map.set(Math.floor(stamp / 3600000), item);
    }
    return map;
  }, [data]);

  const summary12 = { maxTemp: maxValue(next12, 'temperatureF'), minRh: minValue(next12, 'relativeHumidity'), max20: maxValue(next12, 'twentyFootWindSpeedMph'), maxGust: maxValue(next12, 'windGustMph'), maxHaines: maxValue(next12, 'hainesIndex'), maxLal: maxValue(next12, 'lightningActivityLevel'), maxPop: maxValue(next12, 'precipitationProbability') };
  const summary24 = { maxTemp: maxValue(next24, 'temperatureF'), minRh: minValue(next24, 'relativeHumidity'), max20: maxValue(next24, 'twentyFootWindSpeedMph'), maxGust: maxValue(next24, 'windGustMph'), maxHaines: maxValue(next24, 'hainesIndex'), maxLal: maxValue(next24, 'lightningActivityLevel') };

  if (!data && status === 'error') {
    return <div className="firewx-root"><div className="firewx-empty"><span className="firewx-kicker">NWS FIRE WEATHER · CURRENT LOCATION</span><h3>Fire Weather</h3><p>{message}</p>{error ? <div className="firewx-alert neutral">{error}</div> : null}<button type="button" onClick={() => locateAndRefresh(true)}>Use My Location</button></div></div>;
  }

  return (
    <div className="firewx-root">
      <div className="firewx-head">
        <div><span className="firewx-kicker">NATIONAL WEATHER SERVICE · INCIDENT FIRE WEATHER</span><h3>Fire Weather — My Location</h3><p>{data?.location?.fireWeatherZone ? `${data.location.fireWeatherZone}${data.location.fireWeatherZoneId ? ` (${data.location.fireWeatherZoneId})` : ''}` : 'Current NWS forecast grid'}{data?.location?.forecastOffice ? ` · ${data.location.forecastOffice}` : ''}</p></div>
        <div className="firewx-head-actions"><span className={`firewx-state ${status}`}>{offline || !onlineNow() ? 'OFFLINE CACHE' : refreshing ? 'UPDATING' : 'NWS LIVE'}</span><button type="button" disabled={refreshing || !onlineNow()} onClick={() => refreshSavedForecast(true)}>{refreshing ? 'Refreshing…' : 'Refresh Now'}</button><button type="button" className="firewx-location-button" disabled={refreshing || !onlineNow()} onClick={() => locateAndRefresh(true)}>Update Location</button></div>
      </div>

      <div className="firewx-source-line"><div><strong>{message}</strong><span>{data?.fetchedAt ? `Updated ${ageText(data.fetchedAt)} · ${dateTimeLabel(data.fetchedAt, timeZone)}` : 'Waiting for first update'} · auto-refreshes about hourly while active</span></div><span>Forecast location is saved at ~2 km precision. Use Update Location after moving to a different incident.</span></div>
      {error ? <div className="firewx-alert neutral">{error}</div> : null}

      {fireAlerts.map(alert => <div key={alert.id || alert.headline} className="firewx-alert danger"><strong>{alert.event}</strong><span>{alert.headline || alert.description}</span>{alert.ends ? <small>Through {dateTimeLabel(alert.ends, timeZone)}</small> : null}</div>)}

      <section className="firewx-section">
        <div className="firewx-section-head"><div><span className="firewx-kicker">NEAREST LINKED OBSERVATION</span><h4>{station?.name || 'NWS observation station'}</h4></div>{observation?.timestamp ? <small>Observed {ageText(observation.timestamp)}</small> : null}</div>
        <div className="firewx-current-grid">
          <QuickMetric label="Station" value={station?.stationId || '—'} detail={[station?.provider, station?.subProvider].filter(Boolean).join(' · ')} />
          <QuickMetric label="Distance" value={metric(station?.distanceMiles, ' mi')} />
          <QuickMetric label="Elevation" value={metric(station?.elevationFt, ' ft')} />
          <QuickMetric label="Observed Temp" value={metric(observation?.temperatureF ?? current.temperatureF, '°')} detail={observation?.textDescription || 'NWS grid when observation unavailable'} />
          <QuickMetric label="Observed RH" value={metric(observation?.relativeHumidity ?? current.relativeHumidity, '%')} />
          <QuickMetric label="Surface Wind" value={windText(observation?.windDirectionText || current.windDirectionText, observation?.windSpeedMph ?? current.windSpeedMph, observation?.windGustMph ?? current.windGustMph)} />
          <QuickMetric label="20-ft Forecast Wind" value={windText(current.twentyFootWindDirectionText, current.twentyFootWindSpeedMph, current.windGustMph)} detail="NWS fire-weather grid" />
          <QuickMetric label="Mixing Height" value={metric(current.mixingHeightFt, ' ft')} />
          <QuickMetric label="Transport Wind" value={transportText(current)} />
          <QuickMetric label="Haines" value={metric(current.hainesIndex)} detail={current.hainesIndex === 6 ? 'High' : current.hainesIndex === 5 ? 'Moderate' : ''} />
          <QuickMetric label="LAL" value={metric(current.lightningActivityLevel)} />
          <QuickMetric label="Dispersion" value={metric(current.dispersionIndex)} />
          <QuickMetric label="Red Flag Threat" value={metric(current.redFlagThreatIndex)} />
        </div>
      </section>

      <section className="firewx-summary-grid"><ObservationTrend hours={12} rows={observationHistory} /><ObservationTrend hours={24} rows={observationHistory} /></section>

      <section className="firewx-summary-grid">
        <div className="firewx-summary-card"><span className="firewx-kicker">NEXT 12 HOURS · FORECAST</span><div className="firewx-summary-values"><QuickMetric label="Max Temp" value={metric(summary12.maxTemp, '°')} /><QuickMetric label="Min RH" value={metric(summary12.minRh, '%')} /><QuickMetric label="Max 20-ft Wind" value={metric(summary12.max20, ' mph')} /><QuickMetric label="Max Gust" value={metric(summary12.maxGust, ' mph')} /><QuickMetric label="Max Haines" value={metric(summary12.maxHaines)} /><QuickMetric label="Max LAL" value={metric(summary12.maxLal)} /><QuickMetric label="Max PoP" value={metric(summary12.maxPop, '%')} /></div></div>
        <div className="firewx-summary-card"><span className="firewx-kicker">NEXT 24 HOURS · FORECAST</span><div className="firewx-summary-values"><QuickMetric label="Max Temp" value={metric(summary24.maxTemp, '°')} /><QuickMetric label="Min RH" value={metric(summary24.minRh, '%')} /><QuickMetric label="Max 20-ft Wind" value={metric(summary24.max20, ' mph')} /><QuickMetric label="Max Gust" value={metric(summary24.maxGust, ' mph')} /><QuickMetric label="Max Haines" value={metric(summary24.maxHaines)} /><QuickMetric label="Max LAL" value={metric(summary24.maxLal)} /></div></div>
      </section>

      {data?.fireWeatherForecast?.text ? (
        <section className="firewx-section">
          <div className="firewx-section-head"><div><span className="firewx-kicker">NWS TEXT PRODUCT · FWF</span><h4>{data.fireWeatherForecast.productName || 'Fire Weather Planning Forecast'}</h4></div><small>{data.fireWeatherForecast.issuanceTime ? `Issued ${dateTimeLabel(data.fireWeatherForecast.issuanceTime, timeZone)}` : data?.location?.cwa || ''}</small></div>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 440, overflow: 'auto', margin: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, lineHeight: 1.45 }}>{data.fireWeatherForecast.text}</pre>
        </section>
      ) : null}

      <section className="firewx-section">
        <div className="firewx-section-head"><div><span className="firewx-kicker">HOURLY FIRE WEATHER</span><h4>Next 24 hours</h4></div><small>Official NWS digital forecast grid</small></div>
        <div className="firewx-table-wrap"><table className="firewx-table"><thead><tr><th>Time</th><th>Wx</th><th>Temp</th><th>RH</th><th>20-ft Wind</th><th>Gust</th><th>Haines</th><th>LAL</th><th>Mix Hgt</th><th>Transport</th><th>PoP</th></tr></thead><tbody>{next24.map(row => { const stamp = Date.parse(row.time || ''); const general = generalByHour.get(Math.floor(stamp / 3600000)); return <tr key={row.time}><td>{timeLabel(row.time, timeZone)}</td><td>{general?.shortForecast || '—'}</td><td>{metric(row.temperatureF, '°')}</td><td>{metric(row.relativeHumidity, '%')}</td><td>{windText(row.twentyFootWindDirectionText, row.twentyFootWindSpeedMph, null)}</td><td>{metric(row.windGustMph, ' mph')}</td><td>{metric(row.hainesIndex)}</td><td>{metric(row.lightningActivityLevel)}</td><td>{metric(row.mixingHeightFt, ' ft')}</td><td>{transportText(row)}</td><td>{metric(row.precipitationProbability ?? general?.probabilityOfPrecipitation, '%')}</td></tr>; })}</tbody></table></div>
      </section>

      {otherAlerts.length ? <section className="firewx-section"><div className="firewx-section-head"><div><span className="firewx-kicker">OTHER NWS ALERTS</span><h4>Active at this location</h4></div></div><div className="firewx-other-alerts">{otherAlerts.map(alert => <div key={alert.id || alert.headline}><strong>{alert.event}</strong><span>{alert.headline || alert.description}</span></div>)}</div></section> : null}

      <div className="firewx-links">
        {data?.links?.forecast ? <a href={data.links.forecast} target="_blank" rel="noreferrer">NWS Point Forecast ↗</a> : null}
        {data?.links?.station ? <a href={data.links.station} target="_blank" rel="noreferrer">Observation Station ↗</a> : null}
        {data?.links?.spotForecast ? <a href={data.links.spotForecast} target="_blank" rel="noreferrer">NWS Spot Forecast Portal ↗</a> : null}
        {data?.links?.nationalFireWeather ? <a href={data.links.nationalFireWeather} target="_blank" rel="noreferrer">NWS Fire Weather ↗</a> : null}
        {data?.links?.spcFireWeatherOutlook ? <a href={data.links.spcFireWeatherOutlook} target="_blank" rel="noreferrer">SPC Fire Weather Outlook ↗</a> : null}
      </div>

      <p className="firewx-footnote">Automated field reference generated from official NWS forecast, alert, text-product, and linked observation data for the phone’s saved approximate location. Observations and forecasts are labeled separately. This is not a forecaster-issued Spot Forecast and does not replace incident weather briefings or required operational decision processes. NWS offices and observation stations do not populate every parameter at every location; unavailable values are shown as —. The last successful package is saved on this device for offline use.</p>
    </div>
  );
}
